import * as Tooltip from '~/components/ui/Tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shadcn/components/ui/table';
import { useGetFunctions } from '@shadcn/hooks/use-edge-functions';
import { Alert } from '~/components/ui/Alert';
import { Skeleton } from '~/components/ui/Skeleton';
import { supabaseStore } from '~/lib/stores/supabase';
import { useStore } from '@nanostores/react';
import { useMemo, useState } from 'react';
import { IconButton } from '~/components/ui/IconButton';
import { COPIED_MESSAGE_TIMEOUT } from '~/lib/constants';
import { usePaginatedData } from '~/lib/hooks/usePaginatedData';
import { Pagination } from '~/components/ui/Pagination';
import { SortIndicator } from '~/components/ui/SortIndicator';
import { classNames } from '~/utils/classNames';
import { Button } from '~/components/ui/Button';
import { EdgeFunctionsLogs } from './EdgeFunctionLogs';

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SORT_FIELD = 'created_at';
const DEFAULT_SORT_DIRECTION: 'ASC' | 'DESC' = 'ASC';

export function EdgeFunctionsManager() {
  const { data, isLoading, error } = useGetFunctions();
  const list = useMemo(() => data ?? [], [data]);
  const project = useStore(supabaseStore.supabaseProject);
  const projectId = project?.id;
  const [copiedMap, setCopiedMap] = useState<Record<string, boolean>>({});
  const [selectedFuncLogs, setSelectedFuncLogs] = useState<null | { name: string; id: string }>(null);

  const {
    data: paginatedFuns,
    page,
    onPageChange,
    totalPages,
  } = usePaginatedData(list, {
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const copyToClipboard = (rowId: string, key: string, value: string) => {
    const cellKey = `${rowId}-${key}`;
    if (copiedMap[cellKey]) return;

    navigator.clipboard.writeText(value);
    setCopiedMap((prev) => ({ ...prev, [cellKey]: true }));

    setTimeout(() => {
      setCopiedMap((prev) => ({ ...prev, [cellKey]: false }));
    }, COPIED_MESSAGE_TIMEOUT);
  };

  const getHeader = (key: string) => {
    if (key === 'slug') {
      return 'URL';
    }
    if (key === 'created_at') {
      return 'Created';
    }
    if (key === 'updated_at') {
      return 'Last Updated';
    }
    if (key === 'version') {
      return 'Deployments';
    }
    return key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getURL = (slug: string) => {
    // if it's a Bolt database, the slug will be the correct URL. Otherwise, we show Supabase URL
    return projectId ? `https://${projectId}.supabase.co/functions/v1/${slug}` : slug;
  };

  const getFormattedValue = (rowId: string, key: string, value: string | number | Object | null) => {
    if ((key === 'created_at' || key === 'updated_at') && (typeof value === 'number' || typeof value === 'string')) {
      return new Date(value).toLocaleString();
    }

    if (key === 'slug' && typeof value === 'string') {
      const url = getURL(value);
      return (
        <div className="flex gap-1 items-center">
          <span className="max-w-92 truncate">{url}</span>
          <IconButton
            size="sm"
            variant="ghost"
            className="mb-1"
            disabled={copiedMap[`${rowId}-${key}`]}
            onClick={() => copyToClipboard(rowId, key, url)}
            icon={copiedMap[`${rowId}-${key}`] ? 'i-ph:check' : 'i-ph:clipboard-text'}
          />
        </div>
      );
    }

    if (value === null) {
      return 'NULL';
    }

    return typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
  };

  const getTooltipValue = (rowId: string, key: string, value: string | number | Object | null) => {
    if (key === 'slug' && typeof value === 'string') {
      return getURL(value);
    }
    return getFormattedValue(rowId, key, value);
  };

  const tableCellClassName =
    'first:pl-6 lg:first:pl-8 last:pr-6 lg:last:pr-8 text-xs text-bolt-ds-textPrimary group-hover:text-bolt-ds-textPrimary min-w-[8rem]';

  if (selectedFuncLogs) {
    return (
      <EdgeFunctionsLogs
        {...selectedFuncLogs}
        onBack={() => {
          setSelectedFuncLogs(null);
        }}
      />
    );
  }

  return (
    <div className="pt-12">
      <h1 className="text-base lg:text-xl font-semibold">Server Functions</h1>
      <p className="hidden lg:block text-sm lg:text-base text-bolt-ds-textSecondary mt-2 mb-6">
        View deployed server functions on your app.
      </p>

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
        </div>
      )}
      {error && (
        <Alert type="danger" title="Error fetching server functions">
          {error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.'}
        </Alert>
      )}
      {!isLoading && paginatedFuns.length > 0 && (
        <div className="w-full flex flex-col gap-4">
          <Table className="border border-bolt-ds-borderOutline rounded-md">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {Object.keys(paginatedFuns[0])
                  .filter((key) => key !== 'id')
                  .map((key, idx) => {
                    return (
                      <TableHead key={key} className={classNames({ 'first:pl-6 lg:first:pl-8 ': idx === 0 })}>
                        <div className="flex items-center gap-1">
                          <span>{getHeader(key)}</span>
                          <SortIndicator
                            activeField={key}
                            sortField={DEFAULT_SORT_FIELD}
                            sortDirection={DEFAULT_SORT_DIRECTION}
                          />
                        </div>
                      </TableHead>
                    );
                  })}
                <TableHead className="pr-6 pr-8">Logs</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(paginatedFuns as any[]).map((func, index) => (
                <TableRow key={func.id || index} className="group hover:bg-bolt-ds-surfaceThree/50 relative">
                  {Object.keys(paginatedFuns[0])
                    .filter((key) => key !== 'id')
                    .map((key) => {
                      return (
                        <TableCell key={key} className={tableCellClassName}>
                          <Tooltip.Root delayDuration={300}>
                            <Tooltip.Trigger asChild>
                              <div className="text-xs font-mono w-fit max-w-96 truncate cursor-default">
                                {getFormattedValue(func.slug, key, func[key])}
                              </div>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                              <Tooltip.Content sideOffset={4} align="start" side="top">
                                <div className="w-96 max-h-96 p-3 z-max">
                                  <pre className="text-xs font-mono whitespace-pre-wrap break-words">
                                    {getTooltipValue(func.slug, key, func[key])}
                                  </pre>
                                </div>
                              </Tooltip.Content>
                            </Tooltip.Portal>
                          </Tooltip.Root>
                        </TableCell>
                      );
                    })}
                  <TableCell className={tableCellClassName}>
                    <Button
                      startIcon="i-heroicons:document-text"
                      size="md"
                      variant="secondary"
                      onClick={() => {
                        setSelectedFuncLogs({ id: func.id, name: func.name });
                      }}
                    >
                      View Logs
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={onPageChange}
            className="rounded-t-[0px] mt-[-16px] border-t-transparent"
          />
        </div>
      )}
      {!isLoading && !error && paginatedFuns.length === 0 && (
        <div className="text-bolt-ds-textTertiary">You don’t have server functions set up yet.</div>
      )}
    </div>
  );
}
